import { useState, useRef, useEffect } from 'react';
import type { EnrollmentStatus } from './types';
import { fetchClasses, fetchSections, fetchStreams, fetchStates, fetchDistricts, fetchCities, createStudent, searchParentByPhone } from '../../services/studentApi';
import type { ClassOption, SectionOption, StreamOption, StateOption, DistrictOption, CityOption } from '../../services/studentApi';
import { fetchTransportRoutes, type TransportRouteRecord } from '../../services/transportRouteApi';
import { routeMonthlyFeeLabel } from './routeFee';
import type { ApiError } from '../../services/api';
import { getOrganizationId, getSchoolId } from '../../services/auth';
import './AddStudent.css';
import { useTranslation } from 'react-i18next';

interface AddStudentProps {
    onClose: () => void;
    onSave: () => void;
}

export interface AddStudentData {
    // Personal
    first_name: string;
    last_name: string;
    date_of_birth: string;
    gender: 'M' | 'F' | 'Other' | '';
    aadhaar_number: string;
    // Address
    address: string;
    state: string;
    district: string;
    city: string;
    postal_code: string;
    country: string;
    // Academic
    enrollment_status: EnrollmentStatus;
    class_name: string;
    section_name: string;
    // Father
    father_first_name: string;
    father_last_name: string;
    father_phone: string;
    father_email: string;
    // Mother
    mother_first_name: string;
    mother_last_name: string;
    mother_phone: string;
    mother_email: string;
}

const INITIAL_FORM: AddStudentData = {
    first_name: '',
    last_name: '',
    date_of_birth: '',
    gender: '',
    aadhaar_number: '',
    address: '',
    state: '',
    district: '',
    city: '',
    postal_code: '',
    country: '',
    enrollment_status: 'active',
    class_name: '',
    section_name: '',
    father_first_name: '',
    father_last_name: '',
    father_phone: '',
    father_email: '',
    mother_first_name: '',
    mother_last_name: '',
    mother_phone: '',
    mother_email: '',
};

type FieldErrors = Partial<Record<keyof AddStudentData, string>>;

export default function AddStudent({ onClose, onSave }: AddStudentProps) {
    const { t } = useTranslation();
    const [form, setForm] = useState<AddStudentData>({ ...INITIAL_FORM });
    const [errors, setErrors] = useState<FieldErrors>({});
    const [saving, setSaving] = useState(false);
    const [apiError, setApiError] = useState<string | null>(null);

    /* ---- Father search state ---- */
    const [fatherSearchPhone, setFatherSearchPhone] = useState('');
    const [fatherSearching, setFatherSearching] = useState(false);
    const [fatherId, setFatherId] = useState<string | null>(null);
    const [fatherSearchMsg, setFatherSearchMsg] = useState<string | null>(null);

    /* ---- Mother search state ---- */
    const [motherSearchPhone, setMotherSearchPhone] = useState('');
    const [motherSearching, setMotherSearching] = useState(false);
    const [motherId, setMotherId] = useState<string | null>(null);
    const [motherSearchMsg, setMotherSearchMsg] = useState<string | null>(null);

    /* ---- API-driven dropdowns ---- */
    const [availableClasses, setAvailableClasses] = useState<ClassOption[]>([]);
    const [availableSections, setAvailableSections] = useState<SectionOption[]>([]);
    const [availableStreams, setAvailableStreams] = useState<StreamOption[]>([]);
    const [availableStates, setAvailableStates] = useState<StateOption[]>([]);
    const [availableDistricts, setAvailableDistricts] = useState<DistrictOption[]>([]);
    const [availableCities, setAvailableCities] = useState<CityOption[]>([]);
    const [availableRoutes, setAvailableRoutes] = useState<TransportRouteRecord[]>([]);
    const [selectedStateId, setSelectedStateId] = useState<number | null>(null);
    const [selectedClassId, setSelectedClassId] = useState('');
    const [selectedSectionId, setSelectedSectionId] = useState('');
    const [selectedStreamId, setSelectedStreamId] = useState('');
    const [selectedRouteId, setSelectedRouteId] = useState('');

    useEffect(() => {
        fetchClasses()
            .then((classes) => setAvailableClasses(classes))
            .catch(() => { /* silently fall back */ });
        fetchStreams()
            .then((streams) => setAvailableStreams(streams))
            .catch(() => { /* silently fall back */ });
        fetchStates()
            .then((states) => setAvailableStates(states))
            .catch(() => { /* silently fall back */ });
        fetchTransportRoutes()
            .then((routes) => setAvailableRoutes(routes))
            .catch(() => { /* silently fall back */ });
    }, []);

    const selectedRoute = availableRoutes.find((r) => r.id === selectedRouteId);

    /* Cascade: when class changes, reload sections */
    useEffect(() => {
        if (selectedClassId) {
            fetchSections(selectedClassId)
                .then((sections) => setAvailableSections(sections))
                .catch(() => setAvailableSections([]));
        } else {
            setAvailableSections([]);
        }
        setSelectedSectionId('');
        handleChange('section_name', '');
    }, [selectedClassId]);

    /* Cascade: when state changes, reload districts and cities */
    useEffect(() => {
        setAvailableDistricts([]);
        setAvailableCities([]);
        handleChange('district', '');
        handleChange('city', '');
        if (selectedStateId !== null) {
            fetchDistricts(selectedStateId)
                .then((districts) => setAvailableDistricts(districts))
                .catch(() => setAvailableDistricts([]));
            fetchCities(selectedStateId)
                .then((cities) => setAvailableCities(cities))
                .catch(() => setAvailableCities([]));
        }
    }, [selectedStateId]);

    /* ---- File upload state ---- */
    const [profilePhoto, setProfilePhoto] = useState<File | null>(null);
    const [profilePreview, setProfilePreview] = useState<string | null>(null);
    const [studentIdDoc, setStudentIdDoc] = useState<File | null>(null);
    const [parentIdDoc, setParentIdDoc] = useState<File | null>(null);

    const photoInputRef = useRef<HTMLInputElement>(null);
    const studentIdRef = useRef<HTMLInputElement>(null);
    const parentIdRef = useRef<HTMLInputElement>(null);

    /* ---- Handlers ---- */
    const handleChange = (field: keyof AddStudentData, value: string) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors((prev) => {
                const next = { ...prev };
                delete next[field];
                return next;
            });
        }
    };

    /* ---- Father phone search ---- */
    const handleFatherSearch = async () => {
        const phone = fatherSearchPhone.trim();
        if (!phone) return;
        setFatherSearching(true);
        setFatherSearchMsg(null);
        try {
            const parent = await searchParentByPhone(phone);
            if (parent) {
                setFatherId(parent.id);
                setForm((prev) => ({
                    ...prev,
                    father_first_name: parent.first_name || '',
                    father_last_name: parent.last_name || '',
                    father_phone: parent.phone || '',
                    father_email: parent.email || '',
                }));
                setFatherSearchMsg(t('addStudent.parentFound'));
            } else {
                setFatherSearchMsg(t('addStudent.parentNotFound'));
            }
        } catch {
            setFatherSearchMsg(t('addStudent.parentSearchFailed'));
        } finally {
            setFatherSearching(false);
        }
    };

    const clearFatherSearch = () => {
        setFatherId(null);
        setFatherSearchPhone('');
        setFatherSearchMsg(null);
        setForm((prev) => ({
            ...prev,
            father_first_name: '',
            father_last_name: '',
            father_phone: '',
            father_email: '',
        }));
    };

    /* ---- Mother phone search ---- */
    const handleMotherSearch = async () => {
        const phone = motherSearchPhone.trim();
        if (!phone) return;
        setMotherSearching(true);
        setMotherSearchMsg(null);
        try {
            const parent = await searchParentByPhone(phone);
            if (parent) {
                setMotherId(parent.id);
                setForm((prev) => ({
                    ...prev,
                    mother_first_name: parent.first_name || '',
                    mother_last_name: parent.last_name || '',
                    mother_phone: parent.phone || '',
                    mother_email: parent.email || '',
                }));
                setMotherSearchMsg(t('addStudent.parentFound'));
            } else {
                setMotherSearchMsg(t('addStudent.parentNotFound'));
            }
        } catch {
            setMotherSearchMsg(t('addStudent.parentSearchFailed'));
        } finally {
            setMotherSearching(false);
        }
    };

    const clearMotherSearch = () => {
        setMotherId(null);
        setMotherSearchPhone('');
        setMotherSearchMsg(null);
        setForm((prev) => ({
            ...prev,
            mother_first_name: '',
            mother_last_name: '',
            mother_phone: '',
            mother_email: '',
        }));
    };

    const validate = (): boolean => {
        const errs: FieldErrors = {};
        if (!form.first_name.trim()) errs.first_name = t('addStudent.firstNameRequired');
        // When an existing father is linked, skip father field validation
        if (!fatherId) {
            if (!form.father_first_name.trim()) errs.father_first_name = t('addStudent.fatherFirstNameRequired');
            if (!form.father_phone && !form.father_email) {
                errs.father_phone = t('addStudent.phoneOrEmailRequired');
                errs.father_email = t('addStudent.phoneOrEmailRequired');
            }
            if (form.father_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.father_email)) {
                errs.father_email = t('addStudent.invalidEmailFormat');
            }
        }
        // Mother details are optional — only validate email format if provided
        if (!motherId && form.mother_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.mother_email)) {
            errs.mother_email = t('addStudent.invalidEmailFormat');
        }
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validate()) return;
        setSaving(true);
        setApiError(null);

        try {
            const payload = new FormData();

            // Personal
            payload.append('first_name', form.first_name);
            if (form.last_name) payload.append('last_name', form.last_name);
            if (form.date_of_birth) payload.append('date_of_birth', form.date_of_birth);
            if (form.gender) payload.append('gender', form.gender);
            if (form.aadhaar_number) payload.append('aadhaar_number', form.aadhaar_number);

            // Address
            if (form.address) payload.append('address', form.address);
            if (form.state) payload.append('state', form.state);
            if (form.district) payload.append('district', form.district);
            if (form.city) payload.append('city', form.city);
            if (form.postal_code) payload.append('postal_code', form.postal_code);
            if (form.country) payload.append('country', form.country);

            // Academic
            payload.append('enrollment_status', form.enrollment_status);
            if (selectedClassId) payload.append('current_class_id', selectedClassId);
            if (selectedSectionId) payload.append('current_section_id', selectedSectionId);
            if (selectedStreamId) payload.append('stream_id', selectedStreamId);

            // Transport
            if (selectedRouteId) payload.append('transport_route_id', selectedRouteId);

            // Father — send ID if linked to existing, otherwise send details
            if (fatherId) {
                payload.append('father_id', fatherId);
            } else {
                payload.append('parent_first_name', form.father_first_name);
                if (form.father_last_name) payload.append('father_last_name', form.father_last_name);
                if (form.father_phone) payload.append('parent_phone', form.father_phone);
                if (form.father_email) payload.append('father_email', form.father_email);
            }

            // Mother — optional; send ID if linked to existing, otherwise send any provided details
            if (motherId) {
                payload.append('mother_id', motherId);
            } else {
                if (form.mother_first_name) payload.append('mother_first_name', form.mother_first_name);
                if (form.mother_last_name) payload.append('mother_last_name', form.mother_last_name);
                if (form.mother_phone) payload.append('mother_phone', form.mother_phone);
                if (form.mother_email) payload.append('mother_email', form.mother_email);
            }

            // Required tenant context IDs stored at login
            const orgId = getOrganizationId();
            const schoolId = getSchoolId();
            if (orgId) payload.append('organization_id', orgId);
            if (schoolId) payload.append('school_id', schoolId);

            // Optional file uploads
            if (profilePhoto) payload.append('profile_photo', profilePhoto);

            await createStudent(payload);
            onSave();
        } catch (err: unknown) {
            const apiErr = err as ApiError;
            if (apiErr.errors) {
                const fieldErrs: FieldErrors = {};
                for (const [key, msgs] of Object.entries(apiErr.errors)) {
                    fieldErrs[key as keyof AddStudentData] = msgs[0];
                }
                setErrors(fieldErrs);
            }
            setApiError(apiErr.message || t('addStudent.failedToRegister'));
        } finally {
            setSaving(false);
        }
    };

    /* ---- Render helpers ---- */
    const fieldClass = (field: keyof AddStudentData) =>
        `add-student__input ${errors[field] ? 'add-student__input--error' : ''}`;

    /* ---- File handlers ---- */
    const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] ?? null;
        setProfilePhoto(file);
        if (file) {
            const reader = new FileReader();
            reader.onload = (ev) => setProfilePreview(ev.target?.result as string);
            reader.readAsDataURL(file);
        } else {
            setProfilePreview(null);
        }
    };

    const removePhoto = () => {
        setProfilePhoto(null);
        setProfilePreview(null);
        if (photoInputRef.current) photoInputRef.current.value = '';
    };

    const handleDocChange = (
        setter: React.Dispatch<React.SetStateAction<File | null>>,
    ) => (e: React.ChangeEvent<HTMLInputElement>) => {
        setter(e.target.files?.[0] ?? null);
    };

    const removeDoc = (
        setter: React.Dispatch<React.SetStateAction<File | null>>,
        ref: React.RefObject<HTMLInputElement | null>,
    ) => () => {
        setter(null);
        if (ref.current) ref.current.value = '';
    };

    const formatFileSize = (bytes: number) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    return (
        <>
            <div className="add-student__backdrop" onClick={onClose} />

            <aside className="add-student" role="dialog" aria-label="Add new student">
                {/* Header */}
                <header className="add-student__header">
                    <h2 className="add-student__title">{t('addStudent.title')}</h2>
                    <button className="add-student__close" onClick={onClose} title={t('common.close')} aria-label={t('common.close')}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </header>

                {/* API Error Banner */}
                {apiError && (
                    <div className="add-student__error-banner" role="alert">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                            <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            <circle cx="12" cy="16" r="1" fill="currentColor" />
                        </svg>
                        <span>{apiError}</span>
                    </div>
                )}

                {/* Form */}
                <form className="add-student__body" onSubmit={handleSubmit} noValidate>

                    {/* — Personal Information — */}
                    <section className="add-student__section">
                        <h3 className="add-student__section-title">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
                            </svg>
                            {t('addStudent.personalInfo')}
                        </h3>

                        {/* Profile Photo Upload */}
                        <div className="add-student__photo-area">
                            <button
                                type="button"
                                className="add-student__photo-picker"
                                onClick={() => photoInputRef.current?.click()}
                                title="Upload profile photo"
                            >
                                {profilePreview ? (
                                    <img src={profilePreview} alt="Preview" className="add-student__photo-preview" />
                                ) : (
                                    <>
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                            <rect x="3" y="3" width="18" height="18" rx="2" />
                                            <circle cx="8.5" cy="8.5" r="1.5" />
                                            <polyline points="21 15 16 10 5 21" />
                                        </svg>
                                        <span className="add-student__photo-text">{t('addStudent.addPhoto')}</span>
                                    </>
                                )}
                            </button>
                            <input
                                ref={photoInputRef}
                                type="file"
                                accept="image/*"
                                className="add-student__file-hidden"
                                onChange={handlePhotoChange}
                            />
                            {profilePhoto && (
                                <div className="add-student__photo-info">
                                    <span className="add-student__photo-name">{profilePhoto.name}</span>
                                    <button type="button" className="add-student__remove-file" onClick={removePhoto} title="Remove photo">×</button>
                                </div>
                            )}
                            {!profilePhoto && <span className="add-student__photo-hint">{t('addStudent.photoHint')}</span>}
                        </div>

                        <div className="add-student__row">
                            <div className="add-student__field">
                                <label className="add-student__label">
                                    {t('addStudent.firstName')} <span className="add-student__required">*</span>
                                </label>
                                <input
                                    className={fieldClass('first_name')}
                                    type="text"
                                    placeholder="e.g. Aarav"
                                    value={form.first_name}
                                    onChange={(e) => handleChange('first_name', e.target.value)}
                                    autoFocus
                                />
                                {errors.first_name && <span className="add-student__error">{errors.first_name}</span>}
                            </div>
                            <div className="add-student__field">
                                <label className="add-student__label">{t('addStudent.lastName')}</label>
                                <input
                                    className={fieldClass('last_name')}
                                    type="text"
                                    placeholder="e.g. Sharma"
                                    value={form.last_name}
                                    onChange={(e) => handleChange('last_name', e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="add-student__row">
                            <div className="add-student__field">
                                <label className="add-student__label">{t('addStudent.dateOfBirth')}</label>
                                <input
                                    className={fieldClass('date_of_birth')}
                                    type="date"
                                    value={form.date_of_birth}
                                    onChange={(e) => handleChange('date_of_birth', e.target.value)}
                                />
                            </div>
                            <div className="add-student__field">
                                <label className="add-student__label">{t('addStudent.gender')}</label>
                                <select
                                    className={fieldClass('gender')}
                                    value={form.gender}
                                    onChange={(e) => handleChange('gender', e.target.value)}
                                >
                                    <option value="">{t('common.selectGender')}</option>
                                    <option value="M">{t('common.male')}</option>
                                    <option value="F">{t('common.female')}</option>
                                    <option value="Other">{t('common.other')}</option>
                                </select>
                            </div>
                        </div>

                        <div className="add-student__row">
                            <div className="add-student__field">
                                <label className="add-student__label">{t('addStudent.aadhaarNumber')}</label>
                                <input
                                    className={fieldClass('aadhaar_number')}
                                    type="text"
                                    placeholder="e.g. 1234 5678 9012"
                                    value={form.aadhaar_number}
                                    onChange={(e) => handleChange('aadhaar_number', e.target.value)}
                                />
                                {errors.aadhaar_number && <span className="add-student__error">{errors.aadhaar_number}</span>}
                            </div>
                        </div>
                    </section>

                    {/* — Address — */}
                    <section className="add-student__section">
                        <h3 className="add-student__section-title">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
                            </svg>
                            {t('addStudent.address')}
                        </h3>

                        <div className="add-student__field">
                            <label className="add-student__label">{t('addStudent.streetAddress')}</label>
                            <input
                                className={fieldClass('address')}
                                type="text"
                                placeholder={t('addStudent.streetPlaceholder')}
                                value={form.address}
                                onChange={(e) => handleChange('address', e.target.value)}
                            />
                        </div>

                        <div className="add-student__row">
                            <div className="add-student__field">
                                <label className="add-student__label">{t('common.state')}</label>
                                <select
                                    className={fieldClass('state')}
                                    value={form.state}
                                    onChange={(e) => {
                                        const selected = availableStates.find((s) => s.name === e.target.value);
                                        handleChange('state', e.target.value);
                                        setSelectedStateId(selected?.id ?? null);
                                    }}
                                >
                                    <option value="">Select State…</option>
                                    {availableStates.map((s) => (
                                        <option key={s.id} value={s.name}>{s.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="add-student__field">
                                <label className="add-student__label">District</label>
                                <select
                                    className={fieldClass('district')}
                                    value={form.district}
                                    disabled={availableDistricts.length === 0}
                                    onChange={(e) => handleChange('district', e.target.value)}
                                >
                                    <option value="">{selectedStateId === null ? 'Select State first…' : 'Select District…'}</option>
                                    {availableDistricts.map((d) => (
                                        <option key={d.id} value={d.name}>{d.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="add-student__row">
                            <div className="add-student__field">
                                <label className="add-student__label">{t('common.city')}</label>
                                <select
                                    className={fieldClass('city')}
                                    value={form.city}
                                    disabled={availableCities.length === 0}
                                    onChange={(e) => handleChange('city', e.target.value)}
                                >
                                    <option value="">{selectedStateId === null ? 'Select State first…' : 'Select City…'}</option>
                                    {availableCities.map((c) => (
                                        <option key={c.id} value={c.name}>{c.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="add-student__field">
                                <label className="add-student__label">{t('common.postalCode')}</label>
                                <input
                                    className={fieldClass('postal_code')}
                                    type="text"
                                    placeholder="e.g. 226001"
                                    value={form.postal_code}
                                    onChange={(e) => handleChange('postal_code', e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="add-student__row">
                            <div className="add-student__field">
                                <label className="add-student__label">{t('common.country')}</label>
                                <input
                                    className={fieldClass('country')}
                                    type="text"
                                    placeholder="e.g. India"
                                    value={form.country}
                                    onChange={(e) => handleChange('country', e.target.value)}
                                />
                            </div>
                        </div>
                    </section>

                    {/* — Academic Information — */}
                    <section className="add-student__section">
                        <h3 className="add-student__section-title">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M22 10v6M2 10l10-5 10 5-10 5z" /><path d="M6 12v5c0 1.1 2.7 3 6 3s6-1.9 6-3v-5" />
                            </svg>
                            {t('addStudent.academicInfo')}
                        </h3>

                        <div className="add-student__row">
                            <div className="add-student__field">
                                <label className="add-student__label">{t('addStudent.class')}</label>
                                <select
                                    className={fieldClass('class_name')}
                                    value={selectedClassId}
                                    onChange={(e) => {
                                        const id = e.target.value;
                                        setSelectedClassId(id);
                                        const cls = availableClasses.find(c => c.id === id);
                                        handleChange('class_name', cls?.name || '');
                                    }}
                                >
                                    <option value="">{t('addStudent.selectClass')}</option>
                                    {availableClasses.map((c) => (
                                        <option key={c.id} value={c.id}>{c.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="add-student__field">
                                <label className="add-student__label">{t('addStudent.section')}</label>
                                <select
                                    className={fieldClass('section_name')}
                                    value={selectedSectionId}
                                    disabled={!selectedClassId}
                                    onChange={(e) => {
                                        const id = e.target.value;
                                        setSelectedSectionId(id);
                                        const sec = availableSections.find(s => s.id === id);
                                        handleChange('section_name', sec?.name || '');
                                    }}
                                >
                                    <option value="">{t('addStudent.selectSection')}</option>
                                    {availableSections.map((s) => (
                                        <option key={s.id} value={s.id}>{s.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="add-student__row">
                            <div className="add-student__field">
                                <label className="add-student__label">{t('addStudent.stream')}</label>
                                <select
                                    className="add-student__input"
                                    value={selectedStreamId}
                                    onChange={(e) => setSelectedStreamId(e.target.value)}
                                >
                                    <option value="">{t('addStudent.selectStream')}</option>
                                    {availableStreams.map((s) => (
                                        <option key={s.id} value={s.id}>{s.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="add-student__field">
                                <label className="add-student__label">{t('common.status')}</label>
                                <select
                                    className={fieldClass('enrollment_status')}
                                    value={form.enrollment_status}
                                    onChange={(e) => handleChange('enrollment_status', e.target.value)}
                                >
                                    <option value="active">{t('common.active')}</option>
                                    <option value="inactive">{t('common.inactive')}</option>
                                    <option value="graduated">{t('common.graduated')}</option>
                                    <option value="transferred">{t('common.transferred')}</option>
                                </select>
                            </div>
                        </div>
                    </section>

                    {/* — Transport — */}
                    <section className="add-student__section">
                        <h3 className="add-student__section-title">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="3" width="18" height="13" rx="2" /><path d="M3 10h18" />
                                <circle cx="7.5" cy="19" r="1.5" /><circle cx="16.5" cy="19" r="1.5" />
                            </svg>
                            {t('addStudent.transport')}
                        </h3>

                        <div className="add-student__row">
                            <div className="add-student__field">
                                <label className="add-student__label">{t('addStudent.route')}</label>
                                <select
                                    className="add-student__input"
                                    value={selectedRouteId}
                                    onChange={(e) => setSelectedRouteId(e.target.value)}
                                >
                                    <option value="">{t('addStudent.selectRoute')}</option>
                                    {availableRoutes.map((r) => (
                                        <option key={r.id} value={r.id}>{r.title}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="add-student__field">
                                <label className="add-student__label">{t('addStudent.routeMonthlyFee')}</label>
                                <output className="add-student__readout">
                                    {selectedRoute ? routeMonthlyFeeLabel(selectedRoute) : '—'}
                                </output>
                            </div>
                        </div>
                    </section>

                    {/* — Parents — */}
                    <section className="add-student__section">
                        <h3 className="add-student__section-title">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
                            </svg>
                            {t('addStudent.parents')}
                        </h3>

                        {/* Father */}
                        <h4 className="add-student__subsection-title">{t('addStudent.father')}</h4>

                        <div className="add-student__parent-search">
                            <label className="add-student__label">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                                </svg>
                                {t('addStudent.searchParentByPhone')}
                            </label>
                            <div className="add-student__parent-search-row">
                                <input
                                    className="add-student__input add-student__parent-search-input"
                                    type="tel"
                                    placeholder={t('addStudent.searchParentPlaceholder')}
                                    value={fatherSearchPhone}
                                    onChange={(e) => setFatherSearchPhone(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleFatherSearch(); } }}
                                    disabled={!!fatherId}
                                />
                                {fatherId ? (
                                    <button
                                        type="button"
                                        className="add-student__parent-search-btn add-student__parent-search-btn--clear"
                                        onClick={clearFatherSearch}
                                        title={t('common.clear')}
                                    >
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                                        </svg>
                                        {t('common.clear')}
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        className="add-student__parent-search-btn"
                                        onClick={handleFatherSearch}
                                        disabled={fatherSearching || !fatherSearchPhone.trim()}
                                    >
                                        {fatherSearching ? (
                                            <span className="add-student__saving">
                                                <span className="add-student__saving-dot" />
                                                <span className="add-student__saving-dot" />
                                                <span className="add-student__saving-dot" />
                                            </span>
                                        ) : (
                                            <>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                                                </svg>
                                                {t('common.search')}
                                            </>
                                        )}
                                    </button>
                                )}
                            </div>
                            {fatherSearchMsg && (
                                <span className={`add-student__parent-search-msg ${fatherId ? 'add-student__parent-search-msg--found' : 'add-student__parent-search-msg--not-found'}`}>
                                    {fatherId && (
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
                                        </svg>
                                    )}
                                    {fatherSearchMsg}
                                </span>
                            )}
                        </div>

                        <div className="add-student__row">
                            <div className="add-student__field">
                                <label className="add-student__label">
                                    {t('addStudent.firstName')} {!fatherId && <span className="add-student__required">*</span>}
                                </label>
                                <input
                                    className={fieldClass('father_first_name')}
                                    type="text"
                                    placeholder="e.g. Rajesh"
                                    value={form.father_first_name}
                                    onChange={(e) => handleChange('father_first_name', e.target.value)}
                                    readOnly={!!fatherId}
                                />
                                {errors.father_first_name && <span className="add-student__error">{errors.father_first_name}</span>}
                            </div>
                            <div className="add-student__field">
                                <label className="add-student__label">{t('addStudent.lastName')}</label>
                                <input
                                    className={fieldClass('father_last_name')}
                                    type="text"
                                    placeholder="e.g. Sharma"
                                    value={form.father_last_name}
                                    onChange={(e) => handleChange('father_last_name', e.target.value)}
                                    readOnly={!!fatherId}
                                />
                            </div>
                        </div>

                        <div className="add-student__row">
                            <div className="add-student__field">
                                <label className="add-student__label">{t('common.phone')}</label>
                                <input
                                    className={fieldClass('father_phone')}
                                    type="tel"
                                    placeholder="+91 98765 43210"
                                    value={form.father_phone}
                                    onChange={(e) => handleChange('father_phone', e.target.value)}
                                    readOnly={!!fatherId}
                                />
                                {errors.father_phone && <span className="add-student__error">{errors.father_phone}</span>}
                            </div>
                            <div className="add-student__field">
                                <label className="add-student__label">{t('common.email')}</label>
                                <input
                                    className={fieldClass('father_email')}
                                    type="email"
                                    placeholder="parent@mail.com"
                                    value={form.father_email}
                                    onChange={(e) => handleChange('father_email', e.target.value)}
                                    readOnly={!!fatherId}
                                />
                                {errors.father_email && <span className="add-student__error">{errors.father_email}</span>}
                            </div>
                        </div>

                        {/* Mother */}
                        <h4 className="add-student__subsection-title">{t('addStudent.mother')}</h4>

                        <div className="add-student__parent-search">
                            <label className="add-student__label">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                                </svg>
                                {t('addStudent.searchParentByPhone')}
                            </label>
                            <div className="add-student__parent-search-row">
                                <input
                                    className="add-student__input add-student__parent-search-input"
                                    type="tel"
                                    placeholder={t('addStudent.searchParentPlaceholder')}
                                    value={motherSearchPhone}
                                    onChange={(e) => setMotherSearchPhone(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleMotherSearch(); } }}
                                    disabled={!!motherId}
                                />
                                {motherId ? (
                                    <button
                                        type="button"
                                        className="add-student__parent-search-btn add-student__parent-search-btn--clear"
                                        onClick={clearMotherSearch}
                                        title={t('common.clear')}
                                    >
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                                        </svg>
                                        {t('common.clear')}
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        className="add-student__parent-search-btn"
                                        onClick={handleMotherSearch}
                                        disabled={motherSearching || !motherSearchPhone.trim()}
                                    >
                                        {motherSearching ? (
                                            <span className="add-student__saving">
                                                <span className="add-student__saving-dot" />
                                                <span className="add-student__saving-dot" />
                                                <span className="add-student__saving-dot" />
                                            </span>
                                        ) : (
                                            <>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                                                </svg>
                                                {t('common.search')}
                                            </>
                                        )}
                                    </button>
                                )}
                            </div>
                            {motherSearchMsg && (
                                <span className={`add-student__parent-search-msg ${motherId ? 'add-student__parent-search-msg--found' : 'add-student__parent-search-msg--not-found'}`}>
                                    {motherId && (
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
                                        </svg>
                                    )}
                                    {motherSearchMsg}
                                </span>
                            )}
                        </div>

                        <div className="add-student__row">
                            <div className="add-student__field">
                                <label className="add-student__label">{t('addStudent.firstName')}</label>
                                <input
                                    className={fieldClass('mother_first_name')}
                                    type="text"
                                    placeholder="e.g. Sunita"
                                    value={form.mother_first_name}
                                    onChange={(e) => handleChange('mother_first_name', e.target.value)}
                                    readOnly={!!motherId}
                                />
                                {errors.mother_first_name && <span className="add-student__error">{errors.mother_first_name}</span>}
                            </div>
                            <div className="add-student__field">
                                <label className="add-student__label">{t('addStudent.lastName')}</label>
                                <input
                                    className={fieldClass('mother_last_name')}
                                    type="text"
                                    placeholder="e.g. Sharma"
                                    value={form.mother_last_name}
                                    onChange={(e) => handleChange('mother_last_name', e.target.value)}
                                    readOnly={!!motherId}
                                />
                            </div>
                        </div>

                        <div className="add-student__row">
                            <div className="add-student__field">
                                <label className="add-student__label">{t('common.phone')}</label>
                                <input
                                    className={fieldClass('mother_phone')}
                                    type="tel"
                                    placeholder="+91 98765 43210"
                                    value={form.mother_phone}
                                    onChange={(e) => handleChange('mother_phone', e.target.value)}
                                    readOnly={!!motherId}
                                />
                                {errors.mother_phone && <span className="add-student__error">{errors.mother_phone}</span>}
                            </div>
                            <div className="add-student__field">
                                <label className="add-student__label">{t('common.email')}</label>
                                <input
                                    className={fieldClass('mother_email')}
                                    type="email"
                                    placeholder="parent@mail.com"
                                    value={form.mother_email}
                                    onChange={(e) => handleChange('mother_email', e.target.value)}
                                    readOnly={!!motherId}
                                />
                                {errors.mother_email && <span className="add-student__error">{errors.mother_email}</span>}
                            </div>
                        </div>
                    </section>

                    {/* — Documents — */}
                    <section className="add-student__section">
                        <h3 className="add-student__section-title">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14 2 14 8 20 8" />
                            </svg>
                            {t('addStudent.documents')}
                        </h3>
                        <p className="add-student__section-hint">{t('addStudent.documentsHint')}</p>

                        <div className="add-student__row">
                            {/* Student ID */}
                            <div className="add-student__field">
                                <label className="add-student__label">{t('addStudent.studentId')}</label>
                                <div
                                    className={`add-student__dropzone ${studentIdDoc ? 'add-student__dropzone--has-file' : ''}`}
                                    onClick={() => studentIdRef.current?.click()}
                                >
                                    <input
                                        ref={studentIdRef}
                                        type="file"
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        className="add-student__file-hidden"
                                        onChange={handleDocChange(setStudentIdDoc)}
                                    />
                                    {studentIdDoc ? (
                                        <div className="add-student__dropzone-file">
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                <polyline points="14 2 14 8 20 8" />
                                            </svg>
                                            <div className="add-student__dropzone-meta">
                                                <span className="add-student__dropzone-name">{studentIdDoc.name}</span>
                                                <span className="add-student__dropzone-size">{formatFileSize(studentIdDoc.size)}</span>
                                            </div>
                                            <button
                                                type="button"
                                                className="add-student__remove-file"
                                                onClick={(e) => { e.stopPropagation(); removeDoc(setStudentIdDoc, studentIdRef)(); }}
                                                title="Remove file"
                                            >×</button>
                                        </div>
                                    ) : (
                                        <div className="add-student__dropzone-empty">
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                                <polyline points="17 8 12 3 7 8" />
                                                <line x1="12" y1="3" x2="12" y2="15" />
                                            </svg>
                                            <span>{t('common.clickToUpload')}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Parent ID */}
                            <div className="add-student__field">
                                <label className="add-student__label">{t('addStudent.parentGuardianId')}</label>
                                <div
                                    className={`add-student__dropzone ${parentIdDoc ? 'add-student__dropzone--has-file' : ''}`}
                                    onClick={() => parentIdRef.current?.click()}
                                >
                                    <input
                                        ref={parentIdRef}
                                        type="file"
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        className="add-student__file-hidden"
                                        onChange={handleDocChange(setParentIdDoc)}
                                    />
                                    {parentIdDoc ? (
                                        <div className="add-student__dropzone-file">
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                <polyline points="14 2 14 8 20 8" />
                                            </svg>
                                            <div className="add-student__dropzone-meta">
                                                <span className="add-student__dropzone-name">{parentIdDoc.name}</span>
                                                <span className="add-student__dropzone-size">{formatFileSize(parentIdDoc.size)}</span>
                                            </div>
                                            <button
                                                type="button"
                                                className="add-student__remove-file"
                                                onClick={(e) => { e.stopPropagation(); removeDoc(setParentIdDoc, parentIdRef)(); }}
                                                title="Remove file"
                                            >×</button>
                                        </div>
                                    ) : (
                                        <div className="add-student__dropzone-empty">
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                                <polyline points="17 8 12 3 7 8" />
                                                <line x1="12" y1="3" x2="12" y2="15" />
                                            </svg>
                                            <span>{t('common.clickToUpload')}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* — Footer Actions — */}
                    <footer className="add-student__footer">
                        <button type="button" className="add-student__btn add-student__btn--cancel" onClick={onClose}>
                            {t('common.cancel')}
                        </button>
                        <button type="submit" className="add-student__btn add-student__btn--save" disabled={saving}>
                            {saving ? (
                                <span className="add-student__saving">
                                    <span className="add-student__saving-dot" />
                                    <span className="add-student__saving-dot" />
                                    <span className="add-student__saving-dot" />
                                </span>
                            ) : (
                                <>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                                    </svg>
                                    {t('students.addStudent')}
                                </>
                            )}
                        </button>
                    </footer>
                </form>
            </aside>
        </>
    );
}
